// src/pages/OntologyPage.js

import React, { useState } from "react";
import "./OntologyPage.css";

import { createEntityManager } from "../api/EntityManager";

import { buildEntityFromOntology, loadOntology } from "../api/OntologyUtils";

import { useOutletContext } from "react-router-dom";
import { AgentOnly } from "../components/permissions";
import { useAuthWait } from "../auth";

// EXPLICIT list of files to load (from /public/ontology)
const defaultList = [
  "profile",
  "project",
  "room",
  "chatSession",
  "chatMessage",
];

const BASE_PATH = "../ontology"; // put YAML files in public/ontology

function copy(text) {
  if (navigator.clipboard?.writeText)
    return navigator.clipboard.writeText(text);
  const ta = document.createElement("textarea");
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
  return Promise.resolve();
}

function countLines(s) {
  return s ? s.split(/\r\n|\r|\n/).length : 0;
}

export default function OntologyPage() {
  const { userId, awsCreds } = useOutletContext();
  const { user } = useAuthWait();
  const [files, setFiles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [open, setOpen] = React.useState({}); // { [name]: boolean }
  const [managerMeta, setManagerMeta] = React.useState(null);
  const [dataError, setDataError] = React.useState(null);
  const [dataLoading, setDataLoading] = React.useState(true);

  const [relLoading, setRelLoading] = React.useState(false);
  const [relError, setRelError] = React.useState("");
  const [relatedProjects, setRelatedProjects] = React.useState([]);
  const [relationSession, setRelationSession] = React.useState(null);

  const [selectedOntology, setSelectedOntology] = React.useState("");
  const [ontologyObj, setOntologyObj] = React.useState({});
  const [dataText, setDataText] = React.useState("");

  // --- state for generic relations flow ---
  const [relationsOptions, setRelationsOptions] = React.useState([]);
  const [selectedRelationKey, setSelectedRelationKey] = React.useState("");
  const [relatedEntities, setRelatedEntities] = React.useState([]);

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [editedDataText, setEditedDataText] = useState(null); // local override
  const displayDataText = editedDataText ?? dataText; // what we show/copy

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [ctxForm, setCtxForm] = React.useState({
    projectUuid: "",
    roomId: "",
    assetId: "",
    sessionId: "",
    s3Prefix: "",
  });
  const onCtxChange = (e) =>
    setCtxForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  // Build <select> options from ontology.relations
  React.useEffect(() => {
    const rels = ontologyObj?.relations || {};
    const options = [];

    Object.entries(rels).forEach(([relName, arr]) => {
      if (Array.isArray(arr)) {
        arr.forEach((entry, idx) => {
          // e.g. entry = { type: "Project", s3: { bucket, folder_template, id_from } }
          const type = entry?.type || "Unknown";
          const bucket = entry?.s3?.bucket || "";
          const folderTemplate = entry?.s3?.folder_template || "";
          const idFrom = entry?.s3?.id_from || "folder";

          const key = `${relName}:${type}:${idx}`;
          options.push({
            key,
            label: `${relName} → ${type}`,
            name: relName,
            type,
            idx,
            bucket,
            folderTemplate,
            idFrom,
          });
        });
      }
    });

    setRelationsOptions(options);
    // preselect first, if any
    if (options.length && !selectedRelationKey) {
      setSelectedRelationKey(options[0].key);
    }
  }, [ontologyObj]);

  // Extract {TOKEN}s from template like "geovision/{COGNITO_ID}/..."
  function extractTokens(str) {
    const tokens = [];
    str.replace(/\{([^}]+)\}/g, (_, name) => tokens.push(name));
    return tokens;
  }

  // Map known tokens to values from context (case-insensitive).
  // Supports: {COGNITO_ID}, {PROJECT_UUID} (aliases: {UUID}), {ROOM_ID}
  function buildVarsFromTemplate(folderTemplate, context = {}) {
    const tokens = extractTokens(folderTemplate);
    const vars = {};

    for (const rawToken of tokens) {
      const token = String(rawToken).toUpperCase();

      switch (token) {
        case "COGNITO_ID": {
          const val = context.userId || context.cognitoId || context.COGNITO_ID;
          if (!val) {
            throw new Error(
              'Missing "userId/cognitoId" for {COGNITO_ID} in folder_template',
            );
          }
          vars.COGNITO_ID = val;
          break;
        }

        // PROJECT_UUID with common aliases
        case "PROJECT_UUID":
        case "UUID": {
          console.log(context);

          const val =
            context.projectUuid ||
            context.PROJECT_UUID ||
            context.uuid ||
            context.UUID ||
            context.projectId; // permissive alias if you use projectId elsewhere
          if (!val) {
            console.error("Missing 'projectUuid'");
            throw new Error(
              'Missing "projectUuid" for {PROJECT_UUID}/{UUID} in folder_template',
            );
          }
          // Populate both keys so downstream resolvers can use either
          vars.PROJECT_UUID = val;
          vars.UUID = val;
          break;
        }

        case "ROOM_ID": {
          const val = context.roomId || context.ROOM_ID;
          if (!val) {
            throw new Error(
              'Missing "roomId" for {ROOM_ID} in folder_template',
            );
          }
          vars.ROOM_ID = val;
          break;
        }

        default:
          // If you add more tokens in ontologies, extend the switch above.
          throw new Error(
            `No mapping provided for template token "{${rawToken}}"`,
          );
      }
    }

    return vars;
  }

  // --- generic relation explorer ---
  const onExploreRelations = async () => {
    setRelError("");
    setRelLoading(true);
    setRelatedEntities([]);

    try {
      if (!selectedRelationKey)
        throw new Error("Select a relation to explore.");

      const selected = relationsOptions.find(
        (o) => o.key === selectedRelationKey,
      );
      if (!selected) throw new Error("Invalid relation selection.");

      console.log("Selected", ctxForm);
      // Build a small session payload (debug/logging)
      const payload = {
        type: "relations.explore",
        relation: `${selected.name}→${selected.type}`,
        ctx: {
          userId,
          projectUuid: ctxForm.projectUuid,
          roomId: ctxForm.roomId,
          assetId: ctxForm.assetId,
          sessionId: ctxForm.sessionId,
          s3Prefix: ctxForm.s3Prefix,
        },
        ts: new Date().toISOString(),
        note: `Exploring ${selected.name}→${selected.type} via ontology s3.folder_template`,
      };

      console.group("onExploreRelations");
      console.log("session payload:", payload);
      console.groupEnd();

      setRelationSession(payload);

      // Build vars from template tokens (COGNITO_ID, etc.)
      const vars = buildVarsFromTemplate(selected.folderTemplate, payload.ctx);

      // Create manager and list
      const mgr = createEntityManager(ontologyObj, {
        bucketName: selected.bucket || "azrius-data",
      });

      const items = await mgr.list({
        relation: selected.name,
        type: selected.type,
        vars,
        awsCreds,
      });

      setRelatedEntities(items);
    } catch (err) {
      setRelError(err?.message || "Failed to explore selected relation.");
    } finally {
      setRelLoading(false);
    }
  };

  React.useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError("");
      const entries = [];

      try {
        for (const name of defaultList) {
          const url = `${BASE_PATH}/${name}`;
          console.log("fetch:", url);

          try {
            const res = await fetch(url, { cache: "no-store" });
            if (!res.ok) {
              console.warn(`Missing ${name} at ${url}`);
              continue; // skip missing files instead of throwing
            }
            const content = await res.text();
            entries.push({ name, content });
          } catch (err) {
            console.error(`Failed to load ${name}:`, err);
            // continue to next file
          }

          if (!active) return; // if component unmounted mid-loop
        }

        if (active) {
          setFiles(entries);
          if (!entries.length) setError("No YAML files were loaded.");
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, []);

  function resolveVarsFromContext(meta, ctx = {}) {
    // Fill the tokens used in storage.s3.key_template
    const map = {
      COGNITO_ID: ctx.userId,
      UUID: ctx.projectUuid,
      PROJECT_UUID: ctx.projectUuid,
      ROOM_ID: ctx.roomId,
      ASSET_ID: ctx.assetId,
      SESSION_ID: ctx.sessionId,
      s3Prefix: ctx.s3Prefix,
    };
    const vars = {};
    for (const token of meta.placeholders || []) {
      if (map[token] != null) vars[token] = map[token];
    }
    return vars;
  }

  const expandAll = () => {
    const next = {};
    for (const f of files) next[f.name] = true;
    setOpen(next);
  };
  const collapseAll = () => {
    const next = {};
    for (const f of files) next[f.name] = false;
    setOpen(next);
  };
  const toggleOne = (name) => setOpen((o) => ({ ...o, [name]: !o[name] }));

  // make sure `files` is in scope (your state with [{name, content}, ...])
  // replace your onSelectOntology with this
  const onSelectOntology = async (event) => {
    const name = event.target.value;

    if (!name) return;
    setSelectedOntology(name);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      // 1) Parse edited JSON
      const patch = JSON.parse(editValue || "{}");

      // 2) Parse the selected ontology YAML → JS object

      const ontology = await loadOntology(selectedOntology);

      // 3) Build manager & resolve vars from your context (userId + ctxForm tokens)
      const mgr = createEntityManager(ontology);
      const vars = resolveVarsFromContext(mgr.meta, {
        userId, // from useOutletContext()
        projectUuid: ctxForm.projectUuid,
        roomId: ctxForm.roomId,
        assetId: ctxForm.assetId,
        sessionId: ctxForm.sessionId,
        s3Prefix: ctxForm.s3Prefix,
      });

      // 4) Update (shallow merge) the existing entity in S3
      //    - stamps updatedAt/version automatically

      const saved = await mgr.update({
        vars,
        patch,
        aws: awsCreds, // from useOutletContext()
      });

      // 5) Reflect saved content locally; exit edit mode
      const pretty = JSON.stringify(saved, null, 2);
      setEditedDataText(pretty);
      setIsEditing(false);
    } catch (err) {
      console.error("[Ontology Save][error]", err);
      setSaveError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  const onClickLoad = async () => {
    console.debug("onClickLoad");
    if (!selectedOntology) return;

    setDataError?.("");
    setDataLoading?.(true);

    try {
      const ontologyObj = await loadOntology(selectedOntology);

      console.log("[Ontology Load]", ontologyObj);
      setOntologyObj(ontologyObj);
      const mgr = createEntityManager(ontologyObj, awsCreds);

      // build vars from your context form + outlet userId
      const vars = resolveVarsFromContext(mgr.meta, {
        userId, // COGNITO_ID
        projectUuid: ctxForm.projectUuid,
        roomId: ctxForm.roomId,
        assetId: ctxForm.assetId,
        sessionId: ctxForm.sessionId,
        s3Prefix: ctxForm.s3Prefix,
      });

      console.log("Vars:", vars);

      // resolve key (handy for debugging) and GET the entity
      const key = mgr.keyFor({ vars });
      const entity = await mgr.get({ vars, awsCreds });

      console.log("Entity:", entity);
      setDataText(
        entity
          ? JSON.stringify(entity, null, 2)
          : `// No entity found at ${key}\n{}`,
      );
    } catch (err) {
      setDataText("");
      setDataError?.(err?.message || "Failed to load entity.");
    } finally {
      setDataLoading?.(false);
    }
  };

  const handleNew = async () => {
    try {
      setSaveError?.(null);

      if (!selectedOntology) {
        throw new Error("Select an ontology first.");
      }

      const ontologyObj = await loadOntology(selectedOntology);

      // Supply any app-specific tokens your templates use (optional)
      const vars = {
        COGNITO_ID: awsCreds?.identityId,
        PROJECT_UUID: ctxForm.projectUuid,
        ROOM_ID: ctxForm.roomId,
        // ...anything else your key_template/id_pattern reference
      };

      // 1) Build the new entity skeleton
      const entity = buildEntityFromOntology(ontologyObj, { awsCreds, vars });

      // 3) Put into editor; do NOT save
      const body = JSON.stringify(entity, null, 2);
      setEditedDataText?.(null);
      setEditValue?.(body);
      setIsEditing?.(true);
    } catch (err) {
      setSaveError?.(err?.message || String(err));
    }
  };

  return (
    <div className="ontology-page">
      <header className="ontology-header">
        <div>
          <h1 className="ontology-title">Ontology</h1>
          <p className="ontology-subtitle">
            Swingset for exercising the Azrius ontology.
          </p>
        </div>
        <div className="ontology-toolbar">
          <button className="btn" onClick={expandAll}>
            Expand all
          </button>
          <button className="btn" onClick={collapseAll}>
            Collapse all
          </button>
          <button className="btn" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </header>

      {loading && <div className="empty-state">Loading ontology files…</div>}
      {!loading && error && <div className="empty-state">Error: {error}</div>}

      {!loading && !error && files.length > 0 && (
        <>
          {/* YAML file list */}
          <div className="ontology-grid">
            {files.map((file) => {
              const isOpen = !!open[file.name];
              const lines = countLines(file.content);
              return (
                <div className="file-card" key={file.name}>
                  <div className="file-header">
                    <div className="file-name">
                      {file.name}
                      <span className="tag">{lines} lines</span>
                    </div>
                    <div className="file-actions">
                      <button
                        className="btn"
                        onClick={() => toggleOne(file.name)}
                      >
                        {isOpen ? "Collapse" : "Expand"}
                      </button>
                      <button
                        className="btn"
                        onClick={() => copy(file.content)}
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="file-body">
                      <button
                        className="copy-btn"
                        onClick={() => copy(file.content)}
                        title="Copy YAML to clipboard"
                      >
                        Copy
                      </button>
                      <pre
                        className="code-block"
                        aria-label={`${file.name} content`}
                      >
                        {file.content}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="spacer-lg" />

          <div className="file-card">
            <div className="file-header">
              <div className="file-name">
                Context
                <span className="tag">fills {`{TOKENS}`} in key template</span>
              </div>
              <div className="file-actions">
                <button
                  className="btn"
                  onClick={() => selectedOntology}
                  disabled={!selectedOntology}
                  title="Apply context and reload the selected entity"
                >
                  Apply & Load
                </button>
              </div>
            </div>
            <div className="file-body">
              <div className="context-grid">
                <label className="context-label" htmlFor="ctx_userId">
                  userId / {`{COGNITO_ID}`}
                </label>
                <input
                  id="ctx_userId"
                  className="context-input"
                  value={userId || ""}
                  readOnly
                />

                <label className="context-label" htmlFor="ctx_projectUuid">
                  projectUuid / {`{PROJECT_UUID}`}
                </label>
                <input
                  id="ctx_projectUuid"
                  name="projectUuid"
                  className="context-input"
                  value={ctxForm.projectUuid}
                  onChange={onCtxChange}
                />

                <label className="context-label" htmlFor="ctx_roomId">
                  roomId / {`{ROOM_ID}`}
                </label>
                <input
                  id="ctx_roomId"
                  name="roomId"
                  className="context-input"
                  value={ctxForm.roomId}
                  onChange={onCtxChange}
                />

                <label className="context-label" htmlFor="ctx_assetId">
                  assetId / {`{ASSET_ID}`}
                </label>
                <input
                  id="ctx_assetId"
                  name="assetId"
                  className="context-input"
                  value={ctxForm.assetId}
                  onChange={onCtxChange}
                />

                <label className="context-label" htmlFor="ctx_sessionId">
                  sessionId / {`{SESSION_ID}`}
                </label>
                <input
                  id="ctx_sessionId"
                  name="sessionId"
                  className="context-input"
                  value={ctxForm.sessionId}
                  onChange={onCtxChange}
                />

                <label className="context-label" htmlFor="ctx_s3Prefix">
                  s3Prefix
                </label>
                <input
                  id="ctx_s3Prefix"
                  name="s3Prefix"
                  className="context-input"
                  value={ctxForm.s3Prefix}
                  onChange={onCtxChange}
                />

                <div className="context-hint">
                  Only fill the tokens required by the selected ontology’s key
                  template.
                </div>
              </div>
            </div>
          </div>

          {/* Dropdown to load entity data */}
          <div className="spacer-lg" />

          {/* Dropdown to load entity data */}
          <div className="spacer-lg" />

          <div className="file-card">
            <div className="file-header">
              <div className="file-name">
                Choose ontology to load data
                <span className="tag">entity fetcher</span>
              </div>

              <div
                className="file-actions"
                style={{ display: "flex", gap: "0.5rem" }}
              >
                <select
                  id="ontologyDataSelect"
                  className="select"
                  value={selectedOntology || ""}
                  onChange={onSelectOntology}
                  disabled={!files.length}
                  title="Select an ontology"
                >
                  <option value="" disabled>
                    — Select an ontology —
                  </option>
                  {files.map((f) => (
                    <option key={f.name} value={f.name}>
                      {f.name.replace(/\.ya?ml$/, "")}
                    </option>
                  ))}
                </select>

                <button
                  className="btn btn-primary"
                  onClick={onClickLoad}
                  disabled={!selectedOntology}
                  title="Load entity from S3 using the selected ontology"
                >
                  Load
                </button>
              </div>
            </div>

            <div className="file-body">
              {dataLoading ? (
                <pre className="code-block"># Loading…</pre>
              ) : dataError ? (
                <pre className="code-block"># {dataError}</pre>
              ) : (
                <>
                  {/* Actions row (right aligned) */}
                  <div className="file-body-actions">
                    {editedDataText !== null && (
                      <button
                        className="btn"
                        onClick={() => setEditedDataText(null)}
                        title="Revert to originally loaded text"
                      >
                        Revert
                      </button>
                    )}

                    {!isEditing ? (
                      <>
                        <AgentOnly user={user} fallback={null}>
                          <button
                            className="btn btn-primary"
                            onClick={handleNew}
                            disabled={saving || !selectedOntology}
                            title="Create a new entity from the selected ontology and save it"
                          >
                            {saving ? "Creating…" : "New"}
                          </button>
                          <button
                            className="btn"
                            onClick={handleNew}
                            disabled={!displayDataText}
                            title="Edit the loaded text"
                          >
                            Edit
                          </button>
                        </AgentOnly>
                        <button
                          className="btn"
                          onClick={() => copy(displayDataText)}
                          title="Copy text to clipboard"
                          disabled={!displayDataText}
                        >
                          Copy
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="btn btn-primary"
                          onClick={handleSave}
                          disabled={
                            saving ||
                            !editValue ||
                            editValue === displayDataText
                          }
                          title="Save edits to S3 via ontology"
                        >
                          {saving ? "Saving…" : "Save"}
                        </button>
                        {saveError && (
                          <div
                            className="error-text"
                            role="alert"
                            style={{ marginTop: 8 }}
                          >
                            {saveError}
                          </div>
                        )}

                        <button
                          className="btn"
                          onClick={() => setIsEditing(false)}
                          title="Cancel editing"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </div>

                  {/* Viewer / Editor */}
                  {!isEditing ? (
                    <pre className="code-block" aria-label="entity data JSON">
                      {displayDataText ||
                        "# Select an ontology and press Load to fetch data."}
                    </pre>
                  ) : (
                    <textarea
                      className="code-editor"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      spellCheck={false}
                      aria-label="editable entity data"
                    />
                  )}
                </>
              )}
            </div>
          </div>

          <div className="spacer-lg" />

          <div className="file-card">
            <div className="file-header">
              <div className="file-name">
                Relations Explorer
                <span className="tag">via ontology storage</span>
              </div>

              <div
                className="file-actions"
                style={{ display: "flex", gap: "0.5rem" }}
              >
                <select
                  className="select"
                  value={selectedRelationKey}
                  onChange={(e) => setSelectedRelationKey(e.target.value)}
                  title="Choose a relation to explore"
                  disabled={!relationsOptions.length}
                >
                  {relationsOptions.length === 0 ? (
                    <option value="">No relations found</option>
                  ) : (
                    relationsOptions.map((opt) => (
                      <option key={opt.key} value={opt.key}>
                        {opt.label}
                      </option>
                    ))
                  )}
                </select>

                <button
                  className="btn btn-primary"
                  onClick={onExploreRelations}
                  title="List related entities based on ontology"
                  disabled={!relationsOptions.length || !userId}
                >
                  Load
                </button>
              </div>
            </div>

            <div className="file-body">
              {relLoading ? (
                <pre className="code-block"># Loading related entities…</pre>
              ) : relError ? (
                <pre className="code-block"># {relError}</pre>
              ) : relatedEntities.length ? (
                <>
                  <pre className="code-block" aria-label="related json">
                    {JSON.stringify(relatedEntities, null, 2)}
                  </pre>
                </>
              ) : (
                <pre className="code-block"># No related entities found.</pre>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
